import React, { useState } from "react";
import { useLang } from "../i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Mail, Save } from "lucide-react";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function DraftSaveDialog({ open, onOpenChange, defaultEmail, onSave, saving }) {
  const { t } = useLang();
  const [email, setEmail] = useState(defaultEmail || "");
  const [error, setError] = useState("");

  React.useEffect(() => { if (open) { setEmail(defaultEmail || ""); setError(""); } }, [open, defaultEmail]);

  const submit = () => {
    const v = email.trim();
    if (!EMAIL_RE.test(v)) { setError(t("draft.invalidEmail")); return; }
    onSave(v);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="draft-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Save className="w-5 h-5 text-primary" /> {t("draft.title")}</DialogTitle>
          <DialogDescription>{t("draft.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="draft-email" className="text-xs font-semibold text-slate-600">{t("draft.email")}</Label>
          <Input id="draft-email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }}
            data-testid="draft-email-input" className="bg-white" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
          {error && <p className="text-xs text-destructive font-medium" data-testid="draft-email-error">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="draft-cancel-btn">{t("admin.archive.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="draft-send-btn" className="bg-primary hover:bg-primary/90">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            {saving ? t("draft.sending") : t("draft.send")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DraftSavedPanel({ email, onResend, resending, onContinue, onHome }) {
  const { t } = useLang();
  return (
    <div className="bg-card rounded-xl shadow-sm border border-emerald-200 p-8 text-center animate-fade-up" data-testid="draft-saved-panel">
      <span className="grid place-items-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto">
        <Mail className="w-7 h-7" />
      </span>
      <h2 className="mt-5 font-display text-2xl font-bold text-slate-900">{t("draft.savedTitle")}</h2>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-lg mx-auto" data-testid="draft-saved-desc">
        {t("draft.savedDesc", { email })}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button variant="outline" onClick={onResend} disabled={resending} data-testid="draft-resend-btn">
          {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} {t("draft.resend")}
        </Button>
        <Button variant="ghost" onClick={onContinue} data-testid="draft-continue-btn">{t("draft.continue")}</Button>
        <Button onClick={onHome} data-testid="draft-home-btn" className="bg-primary hover:bg-primary/90">{t("draft.home")}</Button>
      </div>
    </div>
  );
}
